import { useEffect, useRef } from 'react';

export default function ServiceArea() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  const cities = [
    'Grand Rapids', 'Kentwood', 'Wyoming', 'Walker', 'Grandville',
    'Jenison', 'Hudsonville', 'Allendale', 'Rockford', 'Sparta',
    'Cedar Springs', 'Lowell', 'Ada', 'Cascade', 'Byron Center',
    'Caledonia', 'Wayland', 'Plainwell', 'Cutlerville', 'Comstock Park',
  ];

  // Boundary towns: Rockford (N), Plainwell (S), Allendale (W), Lowell (E)
  const boundaryPoints: [number, number][] = [
    [43.1203, -85.5583], // Rockford (north)
    [42.6834, -85.6380], // Plainwell (south)
    [42.9726, -85.9552], // Allendale (west)
    [42.9312, -85.3397], // Lowell (east)
  ];

  // Center of the 4 points
  const centerLat = (43.1203 + 42.6834 + 42.9726 + 42.9312) / 4;
  const centerLng = (-85.5583 + -85.6380 + -85.9552 + -85.3397) / 4;

  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;

    // Dynamically load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    // Dynamically load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      const L = (window as any).L;
      if (!mapRef.current || mapInstanceRef.current) return;

      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView(
        [centerLat, centerLng],
        10
      );
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
      }).addTo(map);

      // Draw red ellipse/circle using an ellipse approximation via polygon
      // We'll use a circle centered between all 4 points with radius covering them
      // Calculate radius as max distance from center to any boundary point
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const haversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
        const R = 6371000;
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };

      // Build an ellipse polygon that passes through all 4 boundary towns
      // Semi-axes: latRadius = half N-S span, lngRadius = half E-W span
      const latCenter = (43.1203 + 42.6834) / 2;
      const lngCenter = (-85.9552 + -85.3397) / 2;
      const latRadius = haversine(latCenter, lngCenter, 43.1203, lngCenter) * 1.05;
      const lngRadius = haversine(latCenter, lngCenter, latCenter, -85.3397) * 1.05;

      const ellipsePoints: [number, number][] = [];
      const steps = 120;
      for (let i = 0; i <= steps; i++) {
        const angle = (2 * Math.PI * i) / steps;
        const dLat = (latRadius * Math.sin(angle)) / 111320;
        const dLng =
          (lngRadius * Math.cos(angle)) /
          (111320 * Math.cos(toRad(latCenter)));
        ellipsePoints.push([latCenter + dLat, lngCenter + dLng]);
      }

      L.polygon(ellipsePoints, {
        color: '#ef4444',
        weight: 3,
        fillColor: '#ef4444',
        fillOpacity: 0.12,
        dashArray: '6, 4',
      }).addTo(map);

      // Add markers for the 4 boundary towns
      const boundaryTowns = [
        { name: 'Rockford', coords: [43.1203, -85.5583] as [number, number] },
        { name: 'Plainwell', coords: [42.6834, -85.6380] as [number, number] },
        { name: 'Allendale', coords: [42.9726, -85.9552] as [number, number] },
        { name: 'Lowell', coords: [42.9312, -85.3397] as [number, number] },
      ];

      const redIcon = L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;background:#ef4444;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });

      boundaryTowns.forEach(({ name, coords }) => {
        L.marker(coords, { icon: redIcon })
          .addTo(map)
          .bindTooltip(name, { permanent: true, direction: 'top', offset: [0, -10], className: 'leaflet-tooltip-custom' });
      });

      // Grand Rapids center marker
      const grIcon = L.divIcon({
        className: '',
        html: `<div style="width:12px;height:12px;background:#FACC15;border:2px solid #1F2937;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      L.marker([42.9634, -85.6681], { icon: grIcon })
        .addTo(map)
        .bindTooltip('Grand Rapids', { permanent: true, direction: 'top', offset: [0, -10] });
    };
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center w-16 h-16 bg-yellow-400 rounded-full mx-auto mb-6">
            <i className="ri-map-pin-line text-3xl text-gray-900"></i>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Serving West Michigan</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            BoxFreeMove proudly delivers reusable moving totes throughout the Greater Grand Rapids area — from Rockford to Plainwell and Allendale to Lowell, we've got your move covered.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Interactive Leaflet Map */}
          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-lg mb-10 border border-gray-100">
            <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cities.map((city, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 text-center hover:bg-yellow-50 transition-colors">
                <span className="text-gray-900 font-medium">{city}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-8">
            Not sure if we serve your area? <a href="mailto:hello@boxfreemove.com" className="text-yellow-500 font-semibold hover:underline cursor-pointer">Contact us</a> — if you're in West Michigan, chances are we can help.
          </p>
        </div>
      </div>
    </section>
  );
}
